import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllUsers } from '../../../api/services/adminService';
import UserAvatar from './UserAvatar';
import UserDetailsModal from './UserDetailsModal';
import { SectionHeader, Badge, EmptyState, LoadingSkeleton, Alert, Pagination } from './AdminUI';

/**
 * Parameterized user list filtered by a single role.
 * Replaces the near-identical Landlords/AirbnbHosts/Agents components.
 *
 * @param {string} role     - Role to filter by (e.g. 'LANDLORD', 'BNB_HOST', 'AGENT')
 * @param {string} title    - Page title
 * @param {string} subtitle - Page subtitle
 */
const UsersByRole = ({ role, title, subtitle, detailBasePath }) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [size] = useState(50);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [kycFilter, setKycFilter] = useState('ALL');

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAllUsers(page, size);
      if (response.status === 'success' && response.data) {
        const allUsers = response.data.content || [];
        const filtered = allUsers.filter(
          (user) => user.roles && user.roles.some((r) => String(r).toUpperCase() === role)
        );
        setUsers(filtered);
        setTotalPages(response.data.totalPages || 0);
        setTotalElements(response.data.totalElements || 0);
      }
    } catch (err) {
      setError(err.message || `Failed to load ${title.toLowerCase()}`);
    } finally {
      setLoading(false);
    }
  }, [page, size, role, title]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const filteredUsers = useMemo(() => {
    let filtered = users;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase().includes(q) ||
          user.email?.toLowerCase().includes(q) ||
          user.phone?.toLowerCase().includes(q) ||
          user.id?.toLowerCase().includes(q) ||
          user.idNumber?.toLowerCase().includes(q)
      );
    }
    if (statusFilter !== 'ALL') {
      const isActive = statusFilter === 'ACTIVE';
      filtered = filtered.filter((u) => u.isActive === isActive);
    }
    if (kycFilter !== 'ALL') {
      filtered = filtered.filter((u) => (u.kycStatus || 'NONE') === kycFilter);
    }
    return filtered;
  }, [users, searchQuery, statusFilter, kycFilter]);

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return '—';
    }
  };

  const verifBadge = (status) => {
    if (!status || status === 'NONE') return <Badge status="neutral">Not verified</Badge>;
    if (status === 'APPROVED' || status === 'VERIFIED') return <Badge status="success">Verified</Badge>;
    if (status === 'REJECTED') return <Badge status="danger">Rejected</Badge>;
    return <Badge status="warning">Pending</Badge>;
  };

  const handleViewDetails = (userId) => {
    if (detailBasePath) {
      navigate(`${detailBasePath}/${userId}`);
    } else {
      setSelectedUserId(userId);
      setIsModalOpen(true);
    }
  };

  return (
    <div>
      <SectionHeader
        title={title}
        subtitle={subtitle}
        action={
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              type="text"
              className="admin-btn"
              style={{ width: 200, justifyContent: 'flex-start', color: 'var(--admin-text)', background: 'var(--admin-surface)', border: '1px solid var(--admin-border-strong)' }}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search name, email, phone…"
            />
            <select
              className="admin-btn"
              style={{ color: 'var(--admin-text)', background: 'var(--admin-surface)', border: '1px solid var(--admin-border-strong)' }}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="ALL">All statuses</option>
              <option value="ACTIVE">Active only</option>
              <option value="INACTIVE">Inactive only</option>
            </select>
            <select
              className="admin-btn"
              style={{ color: 'var(--admin-text)', background: 'var(--admin-surface)', border: '1px solid var(--admin-border-strong)' }}
              value={kycFilter}
              onChange={(e) => setKycFilter(e.target.value)}
            >
              <option value="ALL">All KYC</option>
              <option value="APPROVED">KYC Verified</option>
              <option value="PENDING">KYC Pending</option>
              <option value="REJECTED">KYC Rejected</option>
              <option value="NONE">Not submitted</option>
            </select>
          </div>
        }
      />

      {error && <Alert type="error">{error} — <button type="button" onClick={loadUsers} style={{ background: 'none', border: 'none', color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}>retry</button></Alert>}

      {loading ? (
        <div className="admin-table-wrap">
          <LoadingSkeleton lines={8} />
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="admin-table-wrap">
          <EmptyState icon="🔍" title="No users found" text={searchQuery ? 'Try a different search term.' : `No ${title.toLowerCase()} on this page.`} />
        </div>
      ) : (
        <>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Status</th>
                  <th>KYC</th>
                  <th>KYB</th>
                  <th>Last Login</th>
                  <th>Joined</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <UserAvatar profilePhotoPath={user.profilePhotoPath} firstName={user.firstName} lastName={user.lastName} />
                        <span style={{ fontWeight: 600 }}>{user.firstName} {user.lastName}</span>
                      </div>
                    </td>
                    <td>{user.email}</td>
                    <td>{user.phone || '—'}</td>
                    <td>{user.isActive ? <Badge status="success">Active</Badge> : <Badge status="danger">Inactive</Badge>}</td>
                    <td>{verifBadge(user.kycStatus)}</td>
                    <td>{verifBadge(user.kybStatus)}</td>
                    <td style={{ color: 'var(--admin-text-muted)' }}>{formatDate(user.lastLogin)}</td>
                    <td style={{ color: 'var(--admin-text-muted)' }}>{formatDate(user.createdAt)}</td>
                    <td>
                      <button type="button" className="admin-btn admin-btn-outline" onClick={() => handleViewDetails(user.id)}>
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            page={page}
            totalPages={totalPages}
            totalElements={totalElements}
            pageSize={size}
            onPageChange={setPage}
          />
        </>
      )}

      <UserDetailsModal userId={selectedUserId} isOpen={isModalOpen} onClose={() => { setIsModalOpen(false); setSelectedUserId(null); }} />
    </div>
  );
};

export default UsersByRole;
